import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";

// Referral story card (1080x1920) — shareable to Instagram/WhatsApp with user's code.
export function ReferralCard({ user }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !user?.referral_code) return;
    (async () => {
      try { await document.fonts.ready; } catch {}
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const W = 1080, H = 1920;
      canvas.width = W; canvas.height = H;

      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#1a0505");
      bg.addColorStop(1, "#0a0605");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      const rg = ctx.createRadialGradient(W/2, H*0.4, 100, W/2, H*0.4, 950);
      rg.addColorStop(0, "rgba(220,38,38,0.4)");
      rg.addColorStop(1, "rgba(220,38,38,0)");
      ctx.fillStyle = rg;
      ctx.fillRect(0, 0, W, H);

      ctx.globalAlpha = 0.05;
      ctx.fillStyle = "#fff";
      for (let i = 0; i < 400; i++) ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
      ctx.globalAlpha = 1;

      // Wordmark
      ctx.textAlign = "left";
      ctx.font = '700 68px "Outfit", system-ui, sans-serif';
      const nw = ctx.measureText("Next").width;
      const sw = ctx.measureText("Scene").width;
      const startX = W/2 - (nw+sw)/2;
      ctx.fillStyle = "#fff"; ctx.fillText("Next", startX, 180);
      ctx.fillStyle = "#DC2626"; ctx.fillText("Scene", startX + nw, 180);

      // Big invite header
      ctx.textAlign = "center";
      ctx.font = '700 88px "Outfit", system-ui, sans-serif';
      ctx.fillStyle = "#fff";
      ctx.fillText((user.name || "").split(" ")[0] + " ne aapko", W/2, 480);
      ctx.fillStyle = "#DC2626";
      ctx.fillText("invite kiya hai", W/2, 590);

      // Sub headline
      ctx.font = '400 44px "Manrope", system-ui, sans-serif';
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.fillText("Bharat's professional talent stage", W/2, 680);

      // Code card
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.strokeStyle = "#DC2626";
      ctx.lineWidth = 4;
      const cx = W/2, cy = 1050, cw = 780, ch = 340;
      roundRect(ctx, cx-cw/2, cy-ch/2, cw, ch, 40);
      ctx.fill();
      ctx.stroke();

      ctx.font = '500 44px "Outfit", system-ui, sans-serif';
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.fillText("REFERRAL CODE", cx, cy - 60);

      ctx.font = '800 130px "Outfit", system-ui, sans-serif';
      ctx.fillStyle = "#fff";
      ctx.fillText(user.referral_code || "", cx, cy + 80);

      // Reward
      ctx.font = '600 46px "Outfit", system-ui, sans-serif';
      ctx.fillStyle = "#F5C462";
      ctx.fillText("Sign up & we both win.", W/2, 1370);

      ctx.font = '400 40px "Manrope", system-ui, sans-serif';
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillText("5 friends join → free Plus plan for me.", W/2, 1440);

      // CTA
      ctx.font = '400 40px "Manrope", system-ui, sans-serif';
      ctx.fillStyle = "#a1a1aa";
      ctx.fillText("Enter this code at signup", W/2, 1720);

      ctx.font = '700 60px "Manrope", system-ui, sans-serif';
      ctx.fillStyle = "#fff";
      ctx.fillText("nextscene.in", W/2, 1830);
    })();
  }, [user]);

  const download = () => {
    const link = document.createElement("a");
    link.download = `nextscene-invite-${user.referral_code}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
    toast.success("Invite card downloaded");
  };
  const share = async () => {
    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `nextscene-invite-${user.referral_code}.png`, { type: "image/png" });
      try {
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files: [file], title: "Join me on NextScene",
            text: `Join me on NextScene. Use code ${user.referral_code} at signup. Bharat's professional talent stage → nextscene.in`,
          });
          return;
        }
      } catch {}
      download();
    }, "image/png");
  };

  return (
    <div>
      <div className="rounded-2xl overflow-hidden border border-zinc-700 shadow-2xl shadow-[#DC2626]/25 mx-auto max-w-[280px]">
        <canvas ref={canvasRef} className="block w-full h-auto"/>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Button data-testid="ref-card-download" onClick={download} className="rounded-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700">
          <Download size={14} className="mr-2"/> Download
        </Button>
        <Button data-testid="ref-card-share" onClick={share} className="rounded-full h-11 bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          <Share2 size={14} className="mr-2"/> Share
        </Button>
      </div>
    </div>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
